import React, { useState, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import BonusCard from "./BonusCard";
import MultipleBonusDialog from "./MultipleBonusDialog";
import { useBonus } from "../context/BonusContext";
import { useStatic } from "../context/StaticContext";
import { useCss } from "../context/CssContext";

const TALEP_TEXT_MARKER = "__talep_text__";
const extractTalepText = (kurallar) => {
  let talep_text = null;
  const cleanRules = (Array.isArray(kurallar) ? kurallar : []).filter((k) => {
    if (k && k.title === TALEP_TEXT_MARKER) {
      talep_text = k.metin || null;
      return false;
    }
    return true;
  });
  return { talep_text, kurallar: cleanRules };
};

const BonusGrid = () => {
  const { bonusData, loading, gamesData, gamesLoading } = useBonus();
  const { activeTabs, setSelectedBonus, activeMainCategory } = useStatic();
  const { cardLayoutMode } = useCss();
  const isTextOnly = cardLayoutMode === 'text-only';
  const [filteredBonuses, setFilteredBonuses] = useState([]);
  const [multipleBonusDialog, setMultipleBonusDialog] = useState({
    isOpen: false,
    bonuses: [],
  });

  // Aktif ana kategoriye göre kullanılacak veri ve loading durumunu belirle
  const currentData = activeMainCategory === "oyunlar" ? gamesData : bonusData;
  const currentLoading = activeMainCategory === "oyunlar" ? gamesLoading : loading;

  // Metin normalizasyon fonksiyonu
  const normalizeText = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/ü/g, "u")
      .replace(/ı/g, "i")
      .replace(/ö/g, "o")
      .replace(/ğ/g, "g")
      .replace(/ş/g, "s")
      .replace(/ç/g, "c")
      .replace(/\s+/g, ""); // Boşlukları kaldır
  };

  // currentData veya activeTabs değiştiğinde filtreleme yap
  useEffect(() => {
    if (!currentData || currentData.length === 0) {
      setFilteredBonuses([]);
      return;
    }

    // Kategoriları gruplandır (aynı görsel ve tip = çoklu seçenek)
    let groupedItems = [];
    currentData.forEach((category) => {
              if (category.data && Array.isArray(category.data)) {
          // Her seçenekten talep_text'i ayıkla ve kurallar'ı temizle
          const cleanedOptions = category.data.map((item) => {
            const { talep_text, kurallar } = extractTalepText(item.kurallar);
            return { ...item, talep_text: item.talep_text || talep_text, kurallar };
          });
          // Eğer bir kategoride birden fazla seçenek varsa, bu çoklu seçenek demektir
          if (cleanedOptions.length > 1) {
            // Çoklu seçenek için tek bir kart oluştur
            groupedItems.push({
              bonus_name: `${cleanedOptions[0].bonus_name} (${cleanedOptions.length} seçenek)`,
              process_name: `multiple_${cleanedOptions[0].process_name}`,
              bonus_type: category.bonus_type,
              bonus_gorsel: category.bonus_gorsel,
              kurallar: cleanedOptions[0].kurallar || [],
              is_bakim: cleanedOptions.some((item) => item.is_bakim),
              isMultiple: true,
              multipleOptions: cleanedOptions,
              redirect_link: cleanedOptions[0].redirect_link || null,
              talep_text: cleanedOptions[0].talep_text || null,
            });
          } else {
            // Tek seçenek için normal kart oluştur
            cleanedOptions.forEach((item) => {
              groupedItems.push({
                ...item,
                bonus_type: category.bonus_type,
                bonus_gorsel: category.bonus_gorsel,
                isMultiple: false,
              });
            });
          }
        }
      });

      if (activeTabs.includes("tumu")) {
        setFilteredBonuses(groupedItems);
      } else {
        // Tüm aktif sekmeleri normalize et
        const normalizedActiveTabs = activeTabs.map((tab) => normalizeText(tab));

        setFilteredBonuses(
          groupedItems.filter((item) => {
            // item_type string'ini array'e çevir ve normalize et
            const itemTypes = item.bonus_type
              .split(",")
              .map((type) => normalizeText(type));

            // Herhangi bir aktif tab bu item için geçerli mi kontrol et
            return itemTypes.some((type) => normalizedActiveTabs.includes(type));
          })
        );
      }
    }, [currentData, activeTabs]);

  // Çoklu bonus seçimi fonksiyonları
  const handleMultipleBonusRequest = (bonuses) => {
    setMultipleBonusDialog({
      isOpen: true,
      bonuses: bonuses,
    });
  };

  const handleCloseMultipleBonusDialog = () => {
    setMultipleBonusDialog({
      isOpen: false,
      bonuses: [],
    });
  };

  const handleBonusSelect = (selectedBonus) => {
    setSelectedBonus({
      bonus: selectedBonus,
    });
  };

  if (currentLoading) {
    return <div className="bonus-grid-loading">
      {activeMainCategory === "oyunlar" ? "Oyunlar yükleniyor..." : "Bonuslar yükleniyor..."}
    </div>;
  }

  return (
    <>
      <div className={`bonus-grid ${isTextOnly ? "text-only" : ""}`}>
        <AnimatePresence mode="popLayout">
        {filteredBonuses.map((bonus, index) => (
          <BonusCard
            key={`${bonus.process_name}-${index}-${bonus.bonus_name}`}
            cardIndex={index}
            title={bonus.bonus_name}
            imagePath={bonus.bonus_gorsel}
            processName={bonus.process_name}
            bonusType={bonus.bonus_type}
            isMaintenance={bonus.is_bakim}
            rules={bonus.kurallar || []}
            isMultiple={bonus.isMultiple}
            multipleOptions={bonus.multipleOptions}
            redirectLink={bonus.redirect_link}
            confirmText={bonus.talep_text}
            onMultipleBonusRequest={handleMultipleBonusRequest}
          />
        ))}
        </AnimatePresence>
      </div>

      <MultipleBonusDialog
        isOpen={multipleBonusDialog.isOpen}
        onClose={handleCloseMultipleBonusDialog}
        bonuses={multipleBonusDialog.bonuses}
        onBonusSelect={handleBonusSelect}
      />
    </>
  );
};

export default BonusGrid;
